import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Plus, Trash2, ExternalLink, ChevronDown, ChevronUp, X, AlertTriangle, Send, Mail, MoreHorizontal, Search as SearchIcon } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import Gated from "@/components/Gated";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtINR, fmtDate } from "@/lib/leadUtils";
import { PROJECT_STATUSES, PROJECT_STATUS_COLORS, PAYOUT_MODES, daysToGo, eventTime } from "@/lib/projectUtils";
import { useIsMobile } from "@/hooks/useIsMobile";

export default function ProjectDetail() {
  const { can } = useAuth();
  const isMobile = useIsMobile();
  // Phase 6 RBAC — `<Gated resource="...">` reads /auth/whoami/permissions
  // under the hood. We also need `can()` here to conditionally skip API
  // calls that are now server-gated (e.g. /projects/{id}/payouts → pnl_read).
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [confirmations, setConfirmations] = useState({ items: [] });
  const [payouts, setPayouts] = useState({ vendor: [] });
  const [eventModal, setEventModal] = useState(null);
  const [payoutModal, setPayoutModal] = useState(null);
  const [warningModal, setWarningModal] = useState(null);
  const [pnlOpen, setPnlOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  // Phase 7.1 Pass 2B — mobile-only UI state.
  // On mobile the heavy sections (Events / Vendor confirmations / Vendor
  // payouts) are collapsed by default (spec §2 §8 §9 §10). Header overflow
  // menu hosts Open lead + Cancel project (spec §2). Payment-schedule is
  // handled by the existing PaymentScheduleDraft component's own collapse.
  const [eventsOpen, setEventsOpen] = useState(!isMobile);
  const [confirmationsOpen, setConfirmationsOpen] = useState(!isMobile);
  const [payoutsOpen, setPayoutsOpen] = useState(!isMobile);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  // Keep the collapse defaults synced if the viewport crosses the breakpoint
  // mid-session (e.g. rotating a tablet).
  useEffect(() => {
    setEventsOpen(!isMobile);
    setConfirmationsOpen(!isMobile);
    setPayoutsOpen(!isMobile);
  }, [isMobile]);

  const refresh = useCallback(async () => {
    const [pj, cat, vdActive, vdInactive, conf] = await Promise.all([
      api.get(`/projects/${projectId}`),
      api.get("/catalogs"),
      api.get("/vendors"),
      api.get("/vendors", { params: { active: false } }),
      api.get(`/projects/${projectId}/confirmations/preview`),
    ]);
    setData(pj.data);
    setEventTypes((cat.data.event_types || []).filter((e) => e.active).map((e) => e.name));
    setServiceTypes((cat.data.service_types || []).filter((s) => s.active));
    // Merge active + inactive so payouts/services can resolve names of deactivated vendors (Phase 4.3 fix)
    setVendors([...(vdActive.data.items || []), ...(vdInactive.data.items || [])]);
    setConfirmations(conf.data);
    // Phase 6 — /payouts is vendor_payouts_read gated; skip for roles
    // that don't have it (Sales, DM, plus editor of course).
    if (can("vendor_payouts_read")) {
      try {
        const po = await api.get(`/projects/${projectId}/payouts`);
        setPayouts(po.data);
      } catch {
        setPayouts({ vendor: [] });
      }
    } else {
      setPayouts({ vendor: [] });
    }
  }, [projectId, can]);

  useEffect(() => { refresh(); }, [refresh]);

  if (!data) return <Layout><div className="kicker" data-testid="project-loading">Loading</div></Layout>;
  const { project, lead, rollup } = data;
  const dtg = daysToGo(lead.wedding_date);

  const changeStatus = async (newStatus) => {
    try {
      await api.patch(`/projects/${projectId}`, { status: newStatus });
      toast.success(`Status → ${newStatus}`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Status change failed");
    }
  };

  // Slice C: margin = backend-computed P&L profit (revenue_collected − all direct costs).
  // Was previously `quote - vendor - editor - expenses` — wrong because it used
  // quote (booked) instead of revenue_collected (actually paid). For partially-paid
  // projects with high vendor cost this hid losses behind an inflated number.
  const margin = (rollup.profit != null) ? rollup.profit : 0;
  const marginColor = margin < 0 ? "var(--brand-danger)" : (lead.quote_value && margin > lead.quote_value * 0.2) ? "var(--brand-success)" : "var(--brand-ink)";

  // 2026-02-16 — "Vendor paid" pill shows actual cash out (used in margin).
  // Hint under the value contrasts against the *quoted* vendor cost so
  // users see whether the project is on/over/under vendor budget without
  // needing to open the P&L panel or Payouts table. Prevents the class of
  // confusion reported in the Tanuja & Sandeep bug (planned cost mistaken
  // for cash out, or vice versa).
  const vendorQuoted = rollup.vendor_cost_total || 0;
  const vendorPaid = rollup.vendor_payouts_total || 0;
  let vendorHint = null;
  let vendorHintColor = null;
  let vendorHintTitle = null;
  if (vendorQuoted > 0 || vendorPaid > 0) {
    if (vendorPaid > vendorQuoted && vendorQuoted > 0) {
      vendorHint = `${fmtINR(vendorPaid - vendorQuoted)} over budget`;
      vendorHintColor = "var(--brand-danger)";
      vendorHintTitle = `${fmtINR(vendorPaid)} paid vs ${fmtINR(vendorQuoted)} quoted — over budget by ${fmtINR(vendorPaid - vendorQuoted)}`;
    } else if (vendorPaid === 0 && vendorQuoted > 0) {
      vendorHint = `of ${fmtINR(vendorQuoted)} quoted`;
      vendorHintTitle = `Nothing paid yet · ${fmtINR(vendorQuoted)} committed via service quotes`;
    } else if (vendorQuoted === 0 && vendorPaid > 0) {
      vendorHint = "unbudgeted";
      vendorHintColor = "var(--brand-danger)";
      vendorHintTitle = `${fmtINR(vendorPaid)} paid — no vendor cost was quoted on the project structure`;
    } else if (vendorPaid < vendorQuoted) {
      vendorHint = `of ${fmtINR(vendorQuoted)} quoted`;
      vendorHintTitle = `${fmtINR(vendorPaid)} paid of ${fmtINR(vendorQuoted)} quoted · ${fmtINR(vendorQuoted - vendorPaid)} remaining`;
    } else {
      vendorHint = "matches quote";
      vendorHintTitle = `${fmtINR(vendorPaid)} paid — matches the quoted vendor cost exactly`;
    }
  }

  const cancelProject = async () => {
    try {
      await api.post(`/projects/${projectId}/cancel`);
      toast.success("Project cancelled. Lead moved back to Send Proposal.");
      navigate("/projects");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cancel failed");
    }
  };

  return (
    <Layout>
      <div className="fade-up" data-testid="project-detail-page">
        {/* Breadcrumb */}
        <div className="kicker mb-2">
          PRODUCTION · <button onClick={() => navigate("/projects")} className="underline">PROJECTS</button> · {(lead.client_name || "—").toUpperCase()}
        </div>

        {/* Header */}
        <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
          <div className="min-w-0 flex-1">
            <h1
              className="font-display"
              data-testid="project-client-name"
              style={{
                fontSize: isMobile ? "clamp(24px, 6.5vw, 40px)" : undefined,
                lineHeight: isMobile ? 1.1 : undefined,
                wordBreak: isMobile ? "break-word" : "normal",
              }}
            >
              {lead.client_name || "Untitled"}
              {project.partner_name && <span className="text-brand-muted"> &amp; <span style={{ color: "var(--brand-ink)" }} data-testid="project-partner-name">{project.partner_name}</span></span>}
            </h1>
            <div className="text-sm text-brand-muted mt-1">
              Wedding · <span data-testid="project-wedding-date">{fmtDate(lead.wedding_date)}</span> · {lead.wedding_venue || <span className="italic">venue tbd</span>}
              {dtg !== null && (
                <span className="ml-3 px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase" style={{ background: dtg >= 0 ? "var(--brand-success-bg)" : "var(--brand-danger-bg)", color: dtg >= 0 ? "var(--brand-success)" : "var(--brand-danger)" }}>
                  {dtg >= 0 ? `${dtg} days to go` : `${Math.abs(dtg)} days past`}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {/* Desktop-only: inline Open lead + Cancel links (mobile moves both
                into the "···" overflow menu — spec §2). */}
            {!isMobile && (
              <>
                <button onClick={() => navigate(`/leads`)} className="text-sm underline text-brand-muted" data-testid="project-open-lead">
                  <ExternalLink size={12} className="inline -mt-0.5" /> Open lead
                </button>
                {project.status !== "Cancelled" && (
                  <button onClick={() => setCancelModalOpen(true)} className="text-sm text-brand-muted hover:text-brand-rose underline" style={{ color: "var(--brand-danger)" }} data-testid="cancel-project-link">
                    Cancel project
                  </button>
                )}
              </>
            )}
            {project.status === "Cancelled" ? (
              <span className="px-3 py-2 text-sm font-medium text-white" style={{ background: PROJECT_STATUS_COLORS.Cancelled }} data-testid="project-status-cancelled-pill">Cancelled</span>
            ) : (
              <select
                value={project.status}
                onChange={(e) => changeStatus(e.target.value)}
                className="px-3 py-2 text-sm font-medium text-white"
                style={{ background: PROJECT_STATUS_COLORS[project.status], borderRadius: 6, border: 0 }}
                data-testid="project-status-select"
                title="Delivery is confirmed from /delivery; Completed from the Dashboard closure widget."
              >
                {/* Phase 5 Slice B-2 realignment — Project Detail is the PM surface.
                    Show only the statuses PM owns: Confirmed / Shoot done. Add Cancelled
                    here only when project is already Cancelled (won't render anyway since
                    the Cancelled pill branch above takes precedence). Hide Delivered +
                    Completed entirely — those are owned by DM / Owner-closure respectively. */}
                {["Confirmed", "Shoot done"].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
                {/* If somehow project is already past these, keep current label
                    visible so the dropdown isn't empty. */}
                {!["Confirmed", "Shoot done"].includes(project.status) && (
                  <option key={project.status} value={project.status} disabled>
                    {project.status}
                  </option>
                )}
              </select>
            )}
            {/* Mobile-only: "···" overflow menu — Open lead + Cancel project.
                Wired to existing handlers; menu closes on any click. */}
            {isMobile && (
              <div className="relative" data-testid="project-header-menu-wrap">
                <button
                  onClick={() => setHeaderMenuOpen((v) => !v)}
                  data-testid="project-header-menu-button"
                  aria-label="More actions"
                  style={{
                    width: 36, height: 36,
                    borderRadius: 8,
                    background: "#FFFDFA",
                    border: "1px solid #E4DCCD",
                    display: "grid", placeItems: "center",
                    cursor: "pointer",
                    color: "var(--ds-ink)",
                  }}
                >
                  <MoreHorizontal size={17} />
                </button>
                {headerMenuOpen && (
                  <>
                    <div
                      onClick={() => setHeaderMenuOpen(false)}
                      style={{ position: "fixed", inset: 0, zIndex: 40 }}
                    />
                    <div
                      role="menu"
                      data-testid="project-header-menu"
                      style={{
                        position: "absolute",
                        top: "calc(100% + 6px)",
                        right: 0,
                        zIndex: 41,
                        minWidth: 180,
                        background: "#FFFDFA",
                        borderRadius: 12,
                        boxShadow: "0 14px 32px rgba(20,30,24,0.22), 0 0 0 1px rgba(34,56,44,0.06)",
                        overflow: "hidden",
                      }}
                    >
                      <button
                        onClick={() => { setHeaderMenuOpen(false); navigate(`/leads`); }}
                        data-testid="project-open-lead"
                        style={{
                          display: "flex", alignItems: "center", gap: 10,
                          width: "100%", padding: "11px 14px",
                          background: "transparent", border: "none",
                          textAlign: "left", cursor: "pointer",
                          fontSize: 14, color: "var(--ds-ink)",
                        }}
                      >
                        <ExternalLink size={14} /> Open lead
                      </button>
                      {project.status !== "Cancelled" && (
                        <button
                          onClick={() => { setHeaderMenuOpen(false); setCancelModalOpen(true); }}
                          data-testid="cancel-project-link"
                          style={{
                            display: "flex", alignItems: "center", gap: 10,
                            width: "100%", padding: "11px 14px",
                            background: "transparent", border: "none",
                            borderTop: "1px solid rgba(34,56,44,0.06)",
                            textAlign: "left", cursor: "pointer",
                            fontSize: 14, fontWeight: 600, color: "var(--brand-danger)",
                          }}
                        >
                          <Trash2 size={14} /> Cancel project
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Financial strip — Phase 6: gated to pnl_read (Accounts + Owner).
            Mobile (Pass 2B spec §2): 2-column grid + MARGIN spans both columns.

            2026-02-16 fix — bug report: strip was showing "Vendor costs" from
            `vendor_cost_total` (quoted structural cost) while `margin` uses
            `vendor_payouts_total` (actual cash out). Combined with hardcoded
            Editing=0 and Expenses="—", users could not reconcile margin.
            Now the entire strip is cash-basis and arithmetically consistent
            with the P&L panel below:
                Collected − Vendor paid − Editing − Expenses = Margin
        */}
        <Gated resource="pnl_read">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-brand-border mb-6" data-testid="financial-strip">
          <Kpi label="Quote" value={fmtINR(lead.quote_value || 0)} testId="kpi-quote" />
          <Kpi label="Collected" value={fmtINR(rollup.revenue_collected || 0)} testId="kpi-collected" />
          <Kpi label="Vendor paid" value={fmtINR(rollup.vendor_payouts_total || 0)} testId="kpi-vendor-cost" hint={vendorHint} hintColor={vendorHintColor} hintTitle={vendorHintTitle} />
          <Kpi label="Editing" value={fmtINR(rollup.editor_cost_total || 0)} testId="kpi-editing" />
          <Kpi label="Expenses" value={fmtINR(rollup.expenses_total || 0)} testId="kpi-expenses" />
          <div className="col-span-2 sm:col-span-3 lg:col-span-1">
            <Kpi label="Margin" value={fmtINR(margin)} color={marginColor} testId="kpi-margin" />
          </div>
        </div>
        </Gated>

        {/* Advance payment block — Phase 6: gated to pnl_read */}
        <Gated resource="pnl_read">
        {project.advance_payment ? (
          <div className="ds-pill px-5 py-4 mb-6" data-testid="advance-display">
            <div className="kicker mb-1">ADVANCE</div>
            <div className="text-sm">
              <span className="font-display text-xl" style={{ color: "var(--brand-ink)" }}>{fmtINR(project.advance_payment.amount || 0)}</span>
              <span className="text-brand-muted ml-3">
                via {({ upi: "UPI", bank_transfer: "Bank transfer", cash: "Cash", cheque: "Cheque", card: "Card" })[project.advance_payment.mode] || project.advance_payment.mode}
                {project.advance_payment.received_at && ` on ${project.advance_payment.received_at}`}
                {project.advance_payment.reference && ` · ${project.advance_payment.reference}`}
              </span>
            </div>
          </div>
        ) : project.status !== "Cancelled" && (
          <div className="ds-pill px-5 py-3 mb-6 text-xs text-brand-muted italic" data-testid="advance-display-empty">Advance not recorded.</div>
        )}

        {/* Payment schedule draft — Phase 6: gated to pnl_read */}
        {project.payment_schedule_draft && (project.payment_schedule_draft.installments || []).length > 0 && (
          <PaymentScheduleDraft schedule={project.payment_schedule_draft} />
        )}
        </Gated>

        {/* Secondary contacts */}
        {(project.secondary_contacts || []).length > 0 && (
          <div className="mb-6" data-testid="secondary-contacts-section">
            <div className="kicker mb-2">SECONDARY CONTACTS</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {project.secondary_contacts.map((c, i) => (
                <div key={i} className="ds-pill p-3" data-testid={`secondary-contact-${i}`}>
                  <div className="text-sm font-medium">{c.name || "—"}{c.relation && <span className="text-brand-muted text-xs"> · {c.relation}</span>}</div>
                  <div className="text-xs text-brand-muted mt-0.5">{c.phone || "—"}{c.email && ` · ${c.email}`}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Progress banner */}
        <div className="ds-pill px-6 py-4 mb-8 flex flex-wrap gap-8" data-testid="progress-banner">
          <span className="text-sm"><span className="kicker mr-2">SERVICES</span>{rollup.services_assigned} of {rollup.services_total}</span>
          <span className="text-sm"><span className="kicker mr-2">CONFIRMED</span>{rollup.vendors_confirmed} of {rollup.vendors_total}</span>
          <span className="text-sm"><span className="kicker mr-2">DELIVERABLES</span>{rollup.deliverables_delivered ?? 0} / {rollup.deliverables_total ?? 0} delivered{(rollup.deliverables_pending ?? 0) > 0 && <> · {rollup.deliverables_pending} pending</>}</span>
        </div>

        {/* Phase 5 Slice B-2 realignment — sign-off chain moved to Dashboard
            "Projects awaiting closure" widget. Project Detail is the PM
            surface; lifecycle/closure concerns no longer surface here. */}

        {/* Sales notes — Phase 5 Wizard Step 3 Insertion patch.
            Always visible (inline-editable) between project header/progress and events. */}
        <SalesNotesSection projectId={projectId} initial={project.sales_notes || ""} />

        {/* Events section — mobile (spec §8): collapsible via section header,
            collapsed by default. Desktop: always expanded. */}
        <div
          className="flex items-center justify-between mb-4"
          onClick={isMobile ? () => setEventsOpen((v) => !v) : undefined}
          style={{ cursor: isMobile ? "pointer" : "default" }}
          data-testid="events-section-header"
        >
          <h2
            className="font-display text-2xl flex items-center gap-2"
            style={{ color: "var(--brand-ink)" }}
          >
            Events
            {isMobile && (
              <span style={{ color: "var(--ds-muted)" }}>
                {eventsOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </span>
            )}
          </h2>
          <button
            onClick={(e) => { e.stopPropagation(); if (isMobile && !eventsOpen) setEventsOpen(true); setEventModal({ mode: "create" }); }}
            className="px-3 py-2 text-sm font-medium text-white"
            style={{ background: "var(--brand-primary)" }}
            data-testid="add-event-btn"
          >
            <Plus size={14} className="inline -mt-0.5 mr-1" /> Add event
          </button>
        </div>

        {(!isMobile || eventsOpen) && (
        <div className="space-y-5 mb-10" data-testid="events-list">
          {(project.events || []).length === 0 && (
            <div className="ds-pill p-10 text-center" data-testid="events-empty">
              <p className="text-sm text-brand-muted italic">No events yet. Add Haldi, Sangeet, Wedding…</p>
            </div>
          )}
          {(project.events || []).map((ev) => (
            <EventCard
              key={ev.id}
              ev={ev}
              project={project}
              serviceTypes={serviceTypes}
              vendors={vendors}
              payouts={payouts.vendor || []}
              onChanged={refresh}
              onEdit={() => setEventModal({ mode: "edit", event: ev })}
              onWarning={(payload) => setWarningModal(payload)}
            />
          ))}
        </div>
        )}

        {/* Confirmations panel — mobile (spec §9): collapsible, collapsed by default */}
        {(confirmations.items || []).length > 0 && (
          <div className="ds-pill p-6 mb-10" data-testid="confirmations-panel">
            <div
              className="flex items-center justify-between mb-4"
              onClick={isMobile ? () => setConfirmationsOpen((v) => !v) : undefined}
              style={{ cursor: isMobile ? "pointer" : "default" }}
            >
              <h2
                className="font-display text-xl flex items-center gap-2"
                style={{ color: "var(--brand-ink)" }}
              >
                Vendor confirmations
                {isMobile && (
                  <span style={{ color: "var(--ds-muted)" }}>
                    {confirmationsOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </span>
                )}
              </h2>
              {confirmations.items.every((c) => c.confirmation_sent_at) && (
                <span className="px-2 py-1 text-[11px] tracking-[0.16em] uppercase" style={{ background: "var(--brand-success-bg)", color: "var(--brand-success)" }} data-testid="all-confirmed-banner">
                  All vendors confirmed ✓
                </span>
              )}
            </div>
            {(!isMobile || confirmationsOpen) && (
              <div className="space-y-3">
                {confirmations.items.map((c) => (
                  <ConfirmationRow key={c.vendor_id} c={c} projectId={projectId} onSent={refresh} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Payouts panel — mobile (spec §10): collapsible, collapsed by default */}
        <div className="ds-pill p-6 mb-10" data-testid="payouts-panel">
          <div
            className="flex items-center justify-between mb-4"
            onClick={isMobile ? () => setPayoutsOpen((v) => !v) : undefined}
            style={{ cursor: isMobile ? "pointer" : "default" }}
          >
            <h2
              className="font-display text-xl flex items-center gap-2"
              style={{ color: "var(--brand-ink)" }}
            >
              Vendor payouts
              {isMobile && (
                <span style={{ color: "var(--ds-muted)" }}>
                  {payoutsOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </span>
              )}
            </h2>
            <button
              onClick={(e) => { e.stopPropagation(); if (isMobile && !payoutsOpen) setPayoutsOpen(true); setPayoutModal(true); }}
              className="px-3 py-2 text-sm font-medium text-white"
              style={{ background: "var(--brand-primary)" }}
              data-testid="add-payout-btn"
            >
              <Plus size={14} className="inline -mt-0.5 mr-1" /> Payout
            </button>
          </div>
          {(!isMobile || payoutsOpen) && (
            <PayoutsTable project={project} payouts={payouts.vendor || []} vendors={vendors} onChanged={refresh} projectId={projectId} />
          )}
        </div>

        {/* Deliverables panel — Phase 4.3: hidden on this surface (moves to Phase 5 editing dashboard) */}

        {/* P&L panel — Phase 6 RBAC: visible only to Owner + Accounts */}
        <Gated resource="pnl_read">
        <div className="ds-pill p-6" data-testid="pnl-panel">
          <button onClick={() => setPnlOpen(!pnlOpen)} className="w-full flex items-center justify-between" data-testid="pnl-toggle">
            <h2 className="font-display text-xl" style={{ color: "var(--brand-ink)" }}>P&L</h2>
            {pnlOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {pnlOpen && (
            <div className="mt-4 space-y-2 text-sm">
              <PnlRow label="Money in · Collected" value={fmtINR(rollup.revenue_collected || 0)} />
              <PnlRow label="Money out · Vendor payouts" value={fmtINR(-(rollup.vendor_payouts_total || 0))} />
              <PnlRow label="Money out · Editor payouts" value={fmtINR(-(rollup.editor_cost_total || 0))} />
              <PnlRow label="Money out · Project expenses" value={fmtINR(-(rollup.expenses_total || 0))} />
              <PnlRow label="Margin" value={fmtINR(margin)} bold color={marginColor} />
              <div className="text-[10px] text-brand-muted italic pt-1" data-testid="pnl-footer-note">
                Direct costs only. Studio overhead is excluded by design (Slice C rule).
              </div>
              <div className="pt-3">
                <Gated resource="close_project">
                <button
                  onClick={() => changeStatus("Completed")}
                  disabled={project.status !== "Delivered"}
                  className="px-4 py-2 text-sm font-medium text-white disabled:bg-brand-muted"
                  style={{ background: project.status === "Delivered" ? "var(--brand-success)" : "var(--brand-muted)" }}
                  data-testid="mark-completed-btn"
                >
                  Mark Completed
                </button>
                {project.status !== "Delivered" && <span className="ml-3 text-xs text-brand-muted italic">Status must be Delivered first</span>}
                </Gated>
              </div>
            </div>
          )}
        </div>
        </Gated>

        {eventModal && (
          <EventModal
            mode={eventModal.mode}
            event={eventModal.event}
            eventTypes={eventTypes}
            projectId={projectId}
            onClose={() => { setEventModal(null); refresh(); }}
          />
        )}
        {payoutModal && (
          <PayoutModal
            projectId={projectId}
            project={project}
            vendors={vendors}
            onClose={() => { setPayoutModal(false); refresh(); }}
          />
        )}
        {warningModal && (
          <DoubleBookWarningModal
            payload={warningModal.warning}
            onConfirm={async () => {
              await warningModal.confirm();
              setWarningModal(null);
            }}
            onCancel={() => setWarningModal(null)}
          />
        )}
        {cancelModalOpen && (
          <CancelProjectModal
            clientName={lead.client_name}
            partnerName={project.partner_name}
            advanceAmount={(project.advance_payment || {}).amount || 0}
            onConfirm={async () => { setCancelModalOpen(false); await cancelProject(); }}
            onCancel={() => setCancelModalOpen(false)}
          />
        )}
      </div>
    </Layout>
  );
}

function Kpi({ label, value, hint, hintColor, hintTitle, color, testId }) {
  const isMobile = useIsMobile();
  return (
    <div
      className="bg-white"
      style={{
        padding: isMobile ? "12px 14px" : "16px 20px",
        minWidth: 0,
        overflow: "hidden",
      }}
      data-testid={testId}
      title={hintTitle || undefined}
    >
      <div className="kicker mb-1.5">{label}</div>
      <div
        className="font-display"
        style={{
          color: color || "var(--brand-ink)",
          fontSize: isMobile ? "clamp(17px, 5.2vw, 24px)" : 24,
          lineHeight: 1.1,
          wordBreak: isMobile ? "break-word" : "normal",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </div>
      {hint && (
        <div
          className="text-[10px] tracking-[0.18em] uppercase mt-1"
          style={{ color: hintColor || "var(--brand-muted)" }}
          data-testid={testId ? `${testId}-hint` : undefined}
        >
          {hint}
        </div>
      )}
    </div>
  );
}

function PnlRow({ label, value, bold, color }) {
  return (
    <div className="flex justify-between border-b border-brand-border py-2 last:border-0">
      <span style={{ color: "var(--brand-muted)" }}>{label}</span>
      <span style={{ color: color || "var(--brand-ink)", fontWeight: bold ? 600 : 400 }}>{value}</span>
    </div>
  );
}

function EventCard({ ev, project, serviceTypes, vendors, payouts, onChanged, onEdit, onWarning }) {
  const remove = async () => {
    if (!window.confirm(`Delete "${ev.type}"?`)) return;
    try {
      await api.delete(`/projects/${project.id}/events/${ev.id}`);
      onChanged();
    } catch (err) {
      const det = err?.response?.data?.detail;
      if (err.response?.status === 409) {
        if (window.confirm(`${det}\n\nForce delete?`)) {
          await api.delete(`/projects/${project.id}/events/${ev.id}`, { params: { confirm_force: true } });
          onChanged();
        }
      } else {
        toast.error(det || "Delete failed");
      }
    }
  };

  return (
    <div className="ds-pill p-5" data-testid={`event-card-${ev.id}`}>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex-1">
          <button onClick={onEdit} className="text-left hover:underline" data-testid={`event-edit-${ev.id}`}>
            {/* Phase 7.0 Screen F R5 — title 28px Newsreader, 6px gap before date/venue line */}
            <div
              className="font-display"
              style={{ color: "var(--brand-ink)", fontSize: 16, lineHeight: 1.3, marginBottom: 6 }}
            >
              {ev.type}
            </div>
            <div className="text-sm text-brand-muted">
              {ev.date ? fmtDate(ev.date) : <span className="italic">no date</span>}
              {ev.start_time && ev.end_time && <span> · {ev.start_time}–{ev.end_time}</span>}
              {ev.venue ? <span> · {ev.venue}</span> : <span className="italic"> · no venue</span>}
            </div>
          </button>
          <EventTimes ev={ev} project={project} onChanged={onChanged} />
        </div>
        <button onClick={remove} title="Delete event" data-testid={`event-delete-${ev.id}`}>
          <Trash2 size={14} className="text-brand-muted hover:text-brand-rose" />
        </button>
      </div>

      <ServicesTable event={ev} project={project} serviceTypes={serviceTypes} vendors={vendors} payouts={payouts} onChanged={onChanged} onWarning={onWarning} />
    </div>
  );
}

function EventTimes({ ev, project, onChanged }) {
  const [start, setStart] = useState(ev.start_time || "");
  const [end, setEnd] = useState(ev.end_time || "");
  useEffect(() => { setStart(ev.start_time || ""); setEnd(ev.end_time || ""); }, [ev.start_time, ev.end_time]);
  const save = async (field, value) => {
    try {
      await api.patch(`/projects/${project.id}/events/${ev.id}`, { [field]: value || null });
      onChanged();
    } catch (err) { toast.error("Time save failed"); }
  };
  return (
    <div className="flex items-center gap-2 mt-2 text-xs flex-wrap" data-testid={`event-times-${ev.id}`} onClick={(e) => e.stopPropagation()}>
      <span className="kicker">START</span>
      <input type="time" value={start} onChange={(e) => setStart(e.target.value)} onBlur={() => start !== (ev.start_time || "") && save("start_time", start)}
             className="px-2 py-0.5 text-xs border border-brand-border bg-white" style={{ borderRadius: 6, minWidth: 0 }} data-testid={`event-start-${ev.id}`} />
      <span className="kicker">END</span>
      <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} onBlur={() => end !== (ev.end_time || "") && save("end_time", end)}
             className="px-2 py-0.5 text-xs border border-brand-border bg-white" style={{ borderRadius: 6, minWidth: 0 }} data-testid={`event-end-${ev.id}`} />
    </div>
  );
}

function ServicesTable({ event, project, serviceTypes, vendors, payouts, onChanged, onWarning }) {
  const isMobile = useIsMobile();
  const [adding, setAdding] = useState(false);
  const [addType, setAddType] = useState("");
  const [customMode, setCustomMode] = useState(false);

  const services = event.services || [];

  const addSvc = async () => {
    const type = (addType || "").trim();
    if (!type) return;
    const body = { type };
    if (customMode) body.has_deliverables = false;
    try {
      await api.post(`/projects/${project.id}/events/${event.id}/services`, body);
      setAddType(""); setAdding(false); setCustomMode(false);
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || "Add failed"); }
  };

  return (
    <div className="border-t border-brand-border pt-3 -mx-5 px-5" data-testid={`services-table-${event.id}`}>
      {/* Desktop-only column header — hidden on mobile since each service
          renders as a stacked card with inline labels (spec §1). */}
      {!isMobile && (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 240px 110px 90px 140px 28px",
          gap: 12,
          alignItems: "center",
          padding: "0 18px",
          marginBottom: 12,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.13em",
          textTransform: "uppercase",
          color: "#A39A8B",
        }}
      >
        <div>Service</div>
        <div>Vendor</div>
        <div style={{ gridColumn: "3 / 5" }}>Cost ₹ Paid</div>
        <div>Confirmation</div>
        <div />
      </div>
      )}
      {/* Service rows — desktop = grid pill; mobile = stacked card. */}
      <div style={{ display: "flex", flexDirection: "column", gap: isMobile ? 10 : 6 }}>
        {services.map((svc) => (
          <ServiceRow key={svc.id} svc={svc} event={event} project={project} vendors={vendors} payouts={payouts} onChanged={onChanged} onWarning={onWarning} />
        ))}
      </div>
      {adding ? (
        <div className="flex items-center gap-2 pt-3">
          {customMode ? (
            <input autoFocus value={addType} onChange={(e) => setAddType(e.target.value)} placeholder="Custom service name"
                   className="flex-1 fld"
                   data-testid="add-service-custom-input" />
          ) : (
            <select autoFocus value={addType} onChange={(e) => setAddType(e.target.value)}
                    className="flex-1 fld"
                    data-testid={`add-service-type-${event.id}`}>
              <option value="">Pick service type…</option>
              {serviceTypes.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
          )}
          <button onClick={() => setCustomMode(!customMode)} className="text-xs underline text-brand-muted" data-testid={`add-service-custom-toggle-${event.id}`}>
            {customMode ? "Pick from catalog" : "Custom…"}
          </button>
          <button onClick={addSvc} style={{ background: "var(--ds-ink)", color: "#F4EFE4", padding: "9px 16px", fontSize: 13, fontWeight: 600, borderRadius: 8 }} data-testid={`add-service-save-${event.id}`}>Add</button>
          <button onClick={() => { setAdding(false); setAddType(""); setCustomMode(false); }} className="text-brand-muted"><X size={14} /></button>
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          className="mt-3"
          style={{ color: "var(--ds-accent)", fontSize: 13, fontWeight: 500 }}
          data-testid={`add-service-btn-${event.id}`}
        >
          + Add service
        </button>
      )}
    </div>
  );
}

function ServiceRow({ svc, event, project, vendors, payouts, onChanged, onWarning }) {
  const isMobile = useIsMobile();
  const [editingCost, setEditingCost] = useState(false);
  const [costVal, setCostVal] = useState(svc.vendor_cost ?? "");
  const [vendorDropdown, setVendorDropdown] = useState(false);

  // Phase 4.3 — sync local input value when parent refreshes (was stuck on first render value)
  useEffect(() => { setCostVal(svc.vendor_cost ?? ""); }, [svc.vendor_cost]);

  const eligibleVendors = vendors.filter((v) => v.active && (v.services || []).includes(svc.type));

  const navigate = useNavigate();
  const assignedVendor = vendors.find((v) => v.id === svc.vendor_id);

  const saveCost = async () => {
    const n = parseInt(costVal || "0", 10);
    if ((svc.vendor_cost || 0) === n) { setEditingCost(false); return; }
    try {
      await api.patch(`/projects/${project.id}/events/${event.id}/services/${svc.id}`, { vendor_cost: n || null });
      setEditingCost(false);
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const assign = async (vendorId, force = false) => {
    try {
      const res = await api.patch(`/projects/${project.id}/events/${event.id}/services/${svc.id}`,
                                  { vendor_id: vendorId },
                                  force ? { params: { confirm_book: true } } : {});
      if (res.data?.warning) {
        onWarning({
          warning: res.data.warning,
          confirm: () => assign(vendorId, true),
        });
        return;
      }
      setVendorDropdown(false);
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || "Assign failed"); }
  };

  const unassign = async () => {
    if (!window.confirm("Unassign vendor?")) return;
    try {
      await api.patch(`/projects/${project.id}/events/${event.id}/services/${svc.id}`, { clear_vendor: true });
      onChanged();
    } catch (err) {
      // 403 is already surfaced by the global axios interceptor as a quiet
      // toast ("You don't have access to that section.") — and the catch
      // here is what stops the rejection from becoming an UNHANDLED promise
      // rejection, which CRA's react-error-overlay would otherwise turn into
      // a full-screen red crash banner (its `unhandledrejection` listener
      // registers BEFORE ours, so our `preventDefault()` loses the race).
      // For any non-403 error, fall back to a local explicit toast.
      if (err?.response?.status !== 403) {
        toast.error(err?.response?.data?.detail || "Unassign failed");
      }
    }
  };

  const removeSvc = async () => {
    if (!window.confirm(`Remove ${svc.type}?`)) return;
    try {
      await api.delete(`/projects/${project.id}/events/${event.id}/services/${svc.id}`);
      onChanged();
    } catch (err) {
      // See note on `unassign` above — same crash-overlay race + interceptor
      // handles 403 silently.
      if (err?.response?.status !== 403) {
        toast.error(err?.response?.data?.detail || "Remove failed");
      }
    }
  };

  // Phase 7.0 Screen F R4 — single formatted cost string "₹8,000" (₹ inline with the digits,
  // NOT a separate element). Raw digits while editing for natural typing.
  const costDisplay = (() => {
    if (editingCost) return costVal;
    if (costVal === "" || costVal == null) return "";
    const n = parseInt(costVal, 10);
    if (isNaN(n)) return "";
    return `₹${n.toLocaleString("en-IN")}`;
  })();

  // ═══ Mobile stacked-card variant (Pass 2B §1 §8) ═══════════════════════
  // Same handlers, same testids, same PATCH endpoints — just a card body
  // instead of the 6-column grid. Labeled rows for Vendor / Cost / Paid /
  // Confirmation stack vertically for thumb-friendly editing.
  if (isMobile) {
    return (
      <div
        data-testid={`service-row-${svc.id}`}
        style={{
          background: "#FFFDFA",
          borderRadius: 14,
          boxShadow: "0 4px 12px rgba(34,56,44,0.05), 0 1px 2px rgba(34,56,44,0.04)",
          padding: "13px 14px",
          position: "relative",
          zIndex: vendorDropdown ? 40 : "auto",
        }}
      >
        {/* Row 1: service name + delete */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 10 }}>
          <div
            style={{
              fontFamily: "var(--ds-num-font)",
              fontSize: 15,
              fontWeight: 500,
              color: "var(--ds-ink)",
              lineHeight: 1.25,
              flex: 1,
              minWidth: 0,
              wordBreak: "break-word",
            }}
            title={svc.type}
          >
            {svc.type}
          </div>
          <button
            onClick={removeSvc}
            data-testid={`service-delete-${svc.id}`}
            aria-label="Delete service"
            style={{
              color: "#A39A8B",
              width: 28, height: 28,
              padding: 5,
              borderRadius: 6,
              display: "grid", placeItems: "center",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <Trash2 size={14} />
          </button>
        </div>

        {/* Row 2: Vendor */}
        <MobileServiceField label="VENDOR">
          {assignedVendor ? (
            <div
              onClick={() => navigate(`/vendors/${assignedVendor.id}`)}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: 8, background: "#FCFAF5",
                border: "1px solid #E4DCCD", borderRadius: 8,
                padding: "9px 11px", cursor: "pointer",
              }}
            >
              <span
                data-testid={`service-vendor-link-${svc.id}`}
                style={{ fontSize: 14, fontWeight: 600, color: "#3F6B51", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}
              >
                {assignedVendor.name}
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); unassign(); }}
                data-testid={`service-unassign-${svc.id}`}
                aria-label="Unassign"
                style={{ color: "#A39A8B", background: "transparent", border: "none", cursor: "pointer", padding: 4, flexShrink: 0 }}
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setVendorDropdown(true)}
              data-testid={`service-assign-btn-${svc.id}`}
              style={{
                width: "100%", textAlign: "left",
                background: "#FCFAF5", border: "1px solid #E4DCCD",
                borderRadius: 8, padding: "9px 11px",
                fontSize: 14, color: "#A39A8B", cursor: "pointer",
              }}
            >
              Assign vendor…
            </button>
          )}
          {vendorDropdown && (
            <VendorPicker
              event={event}
              eligible={eligibleVendors}
              onPick={(id) => assign(id, false)}
              onClose={() => setVendorDropdown(false)}
              testId={`vendor-picker-${svc.id}`}
              serviceType={svc.type}
            />
          )}
        </MobileServiceField>

        {/* Row 3: Cost / Paid — inline pair */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 10, alignItems: "end", marginTop: 10 }}>
          <MobileServiceField label="COST ₹">
            <input
              type="text"
              inputMode="numeric"
              value={costDisplay}
              disabled={!svc.vendor_id}
              onChange={(e) => {
                const digits = e.target.value.replace(/[^\d]/g, "");
                setCostVal(digits);
              }}
              onFocus={() => setEditingCost(true)}
              onBlur={saveCost}
              onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
              placeholder="—"
              style={{
                width: "100%",
                background: "#FCFAF5",
                border: "1px solid #E4DCCD",
                borderRadius: 8,
                padding: "9px 11px",
                fontSize: 14,
                fontWeight: 500,
                color: svc.vendor_id ? "#22382C" : "#B0A891",
                textAlign: "right",
                fontVariantNumeric: "tabular-nums",
              }}
              data-testid={`service-cost-${svc.id}`}
            />
          </MobileServiceField>
          <div style={{ paddingBottom: 5 }}>
            {svc.vendor_id && svc.vendor_cost > 0 ? (
              <PaidPill svc={svc} project={project} payouts={payouts} />
            ) : (
              <span style={{ color: "#B0A891", fontSize: 13 }}>—</span>
            )}
          </div>
        </div>

        {/* Row 4: Confirmation */}
        <div style={{ marginTop: 10 }}>
          <MobileServiceField label="CONFIRMATION">
            {!svc.vendor_id ? (
              <span style={{ color: "#B0A891", fontSize: 13 }}>—</span>
            ) : svc.confirmation_sent_at ? (
              <span
                data-testid={`confirm-sent-${svc.id}`}
                style={{ color: "#5C6B5E", fontSize: 13, fontWeight: 500 }}
              >
                Sent {fmtDate(svc.confirmation_sent_at)}
              </span>
            ) : (
              <span style={{ color: "#B0A891", fontStyle: "italic", fontSize: 13 }}>Not sent</span>
            )}
          </MobileServiceField>
        </div>
      </div>
    );
  }

  // ═══ Desktop 6-column grid (unchanged) ═════════════════════════════════

  return (
    <div
      data-testid={`service-row-${svc.id}`}
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 240px 110px 90px 140px 28px",
        gap: 12,
        alignItems: "center",
        background: "#FFFFFF",
        border: "1px solid #EDE5D8",
        borderRadius: 10,
        padding: "10px 11px",
        boxShadow: "0 2px 6px rgba(60,46,20,.05)",
        transition: "box-shadow .15s ease, transform .15s ease",
        // R7 picker fix — when the picker is open, lift the entire row above sibling rows
        // (siblings in the parent flex have no z-index and would otherwise paint over the picker).
        position: "relative",
        zIndex: vendorDropdown ? 40 : "auto",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 5px 14px rgba(60,46,20,.10)";
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "0 2px 6px rgba(60,46,20,.05)";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      {/* Col 1 — SERVICE name */}
      <div
        style={{
          fontWeight: 500,
          fontSize: 14,
          color: "#2A2722",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        title={svc.type}
      >
        {svc.type}
      </div>

      {/* Col 2 — VENDOR (fixed 240px). Preserves existing handlers: vendor-name onClick →
          /vendors/:id, × onClick → unassign, "Assign vendor…" onClick → opens VendorPicker.
          NO chevron — there is no native dropdown in this component. */}
      <div className="relative" style={{ minWidth: 0 }}>
        {assignedVendor ? (
          <div
            onClick={() => navigate(`/vendors/${assignedVendor.id}`)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              background: "#FCFAF5",
              border: "1px solid #E4DCCD",
              borderRadius: 8,
              padding: "6px 8px",
              cursor: "pointer",
              transition: "border-color .15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#B08D55")}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#E4DCCD")}
          >
            <span
              data-testid={`service-vendor-link-${svc.id}`}
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: "#3F6B51",
                textDecoration: "none",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                minWidth: 0,
              }}
            >
              {assignedVendor.name}
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); unassign(); }}
              title="Unassign"
              data-testid={`service-unassign-${svc.id}`}
              style={{
                color: "#A39A8B",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                minWidth: 20,
                minHeight: 20,
                marginRight: -4,
                transition: "color .15s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#A35C45")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#A39A8B")}
            >
              <X size={13} />
            </button>
          </div>
        ) : (
          <button
            onClick={() => setVendorDropdown(!vendorDropdown)}
            data-testid={`service-assign-btn-${svc.id}`}
            style={{
              width: "100%",
              textAlign: "left",
              background: "#FCFAF5",
              border: "1px solid #E4DCCD",
              borderRadius: 8,
              padding: "6px 8px",
              fontSize: 14,
              color: "#A39A8B",
              textDecoration: "none",
              transition: "border-color .15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#B08D55")}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#E4DCCD")}
          >
            Assign vendor…
          </button>
        )}
        {vendorDropdown && (
          <VendorPicker
            event={event}
            eligible={eligibleVendors}
            onPick={(id) => assign(id, false)}
            onClose={() => setVendorDropdown(false)}
            testId={`vendor-picker-${svc.id}`}
            serviceType={svc.type}
          />
        )}
      </div>

      {/* Col 3 — COST (110px), .fld box, single inline "₹8,000" string, right-aligned */}
      <div>
        <input
          type="text"
          inputMode="numeric"
          value={costDisplay}
          disabled={!svc.vendor_id}
          onChange={(e) => {
            // Strip everything except digits (handles ₹ prefix + commas on paste)
            const digits = e.target.value.replace(/[^\d]/g, "");
            setCostVal(digits);
          }}
          onFocus={() => setEditingCost(true)}
          onBlur={saveCost}
          onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
          placeholder="—"
          style={{
            width: "100%",
            background: "#FCFAF5",
            border: "1px solid #E4DCCD",
            borderRadius: 8,
            padding: "6px 7px",
            fontSize: 14,
            fontWeight: 500,
            color: svc.vendor_id ? "#22382C" : "#B0A891",
            textAlign: "right",
            fontVariantNumeric: "tabular-nums",
          }}
          data-testid={`service-cost-${svc.id}`}
        />
      </div>

      {/* Col 4 — CONFIRMATION status (90px): Paid sentence-case / PARTIAL gold chip / Unpaid */}
      <div style={{ display: "flex", alignItems: "center" }}>
        {svc.vendor_id && svc.vendor_cost > 0 ? (
          <PaidPill svc={svc} project={project} payouts={payouts} />
        ) : (
          <span style={{ color: "#B0A891", fontSize: 13 }}>—</span>
        )}
      </div>

      {/* Col 5 — SENT/STATUS DATE (130px), muted #9A9387, 13px */}
      <div style={{ minWidth: 0 }}>
        {!svc.vendor_id ? (
          <span style={{ color: "#B0A891", fontSize: 13 }}>—</span>
        ) : svc.confirmation_sent_at ? (
          <span
            data-testid={`confirm-sent-${svc.id}`}
            style={{ color: "#9A9387", fontSize: 13, whiteSpace: "nowrap" }}
          >
            Sent {fmtDate(svc.confirmation_sent_at)}
          </span>
        ) : (
          <span style={{ color: "#B0A891", fontStyle: "italic", fontSize: 13 }}>Not sent</span>
        )}
      </div>

      {/* Col 6 — DELETE icon (28px) centered, min 24px tappable area */}
      <div style={{ display: "flex", justifyContent: "center" }}>
        <button
          onClick={removeSvc}
          data-testid={`service-delete-${svc.id}`}
          style={{
            color: "#A39A8B",
            minWidth: 24,
            minHeight: 24,
            padding: 5,
            borderRadius: 4,
            transition: "color .15s",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#A35C45")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "#A39A8B")}
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

function PaidPill({ svc, project, payouts }) {
  if (!svc.vendor_id) return <span className="text-brand-muted">—</span>;
  if (!svc.vendor_cost) return <span className="text-brand-muted italic">—</span>;
  let owed = 0;
  for (const ev of project.events || []) {
    for (const s of ev.services || []) {
      if (s.vendor_id === svc.vendor_id) owed += parseInt(s.vendor_cost || 0, 10);
    }
  }
  const paid = (payouts || []).filter((p) => p.vendor_id === svc.vendor_id).reduce((a, b) => a + (b.amount || 0), 0);
  // Phase 7.0 Screen F R4 — Paid = sentence-case green text (no chip).
  // Partial = gold chip (#F4EDDF / #9A7A3C / weight 700 / 10px / .1em / 5px 10px / radius 6).
  // Unpaid = sentence-case clay text (no chip — mirrors Paid styling pattern).
  if (paid === 0) {
    return (
      <span
        data-testid={`paid-pill-${svc.id}`}
        style={{ color: "#A35C45", fontWeight: 600, fontSize: 12 }}
      >
        Unpaid
      </span>
    );
  }
  if (paid >= owed) {
    return (
      <span
        data-testid={`paid-pill-${svc.id}`}
        style={{ color: "#3F6B51", fontWeight: 600, fontSize: 12 }}
      >
        Paid
      </span>
    );
  }
  return (
    <span
      data-testid={`paid-pill-${svc.id}`}
      style={{
        background: "#F4EDDF",
        color: "#9A7A3C",
        fontWeight: 700,
        fontSize: 10,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        padding: "4px 8px",
        borderRadius: 6,
        display: "inline-block",
      }}
    >
      Partial
    </span>
  );
}

function VendorPicker({ event, eligible, onPick, onClose, testId, serviceType }) {
  // Phase 4.3 — strict eligibility per spec: "Filtered to vendors who render the selected service type."
  // No opt-out. Vendors whose `services` does NOT include the selected service type are excluded entirely.
  // Phase 7.0 R7: zIndex 50 + isolation:isolate to lift the picker above any sibling row's stacking
  // context (rows have transform-on-hover which would otherwise let the row's content show through).
  // Phase 7.1 Pass 2B §3: on mobile, render as a bottom sheet with a search
  // field (per spec) instead of the desktop hover popover. Same data + onPick.
  const isMobile = useIsMobile();
  const [q, setQ] = useState("");
  const list = eligible;
  const filtered = q
    ? list.filter((v) => (v.name || "").toLowerCase().includes(q.toLowerCase()))
    : list;

  if (isMobile) {
    return (
      <>
        <div
          onClick={onClose}
          data-testid={`${testId}-scrim`}
          style={{
            position: "fixed", inset: 0, zIndex: 10050,
            background: "rgba(20,30,24,0.42)",
            backdropFilter: "blur(3px)", WebkitBackdropFilter: "blur(3px)",
          }}
        />
        <div
          role="dialog"
          aria-modal="true"
          data-testid={testId}
          style={{
            position: "fixed",
            left: 0, right: 0, bottom: 0,
            zIndex: 10051,
            background: "#FFFDFA",
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            boxShadow: "0 -12px 40px rgba(20,30,24,0.28)",
            padding: `18px 18px calc(env(safe-area-inset-bottom, 0px) + 16px)`,
            maxHeight: "82vh",
            display: "flex",
            flexDirection: "column",
            isolation: "isolate",
          }}
        >
          <div aria-hidden="true" style={{ width: 44, height: 5, background: "rgba(34,56,44,0.18)", borderRadius: 999, margin: "0 auto 12px" }} />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <div>
              <div style={{ fontFamily: "var(--ds-num-font)", fontSize: 18, color: "var(--ds-ink)" }}>
                Assign vendor
              </div>
              <div style={{ fontSize: 11, letterSpacing: "0.12em", fontWeight: 700, color: "var(--ds-accent)", textTransform: "uppercase", marginTop: 3 }}>
                For {serviceType}
              </div>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--ds-muted)", padding: 6 }}
            >
              <X size={18} />
            </button>
          </div>
          <div style={{ position: "relative", marginBottom: 12 }}>
            <SearchIcon size={14} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--ds-muted)" }} />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search vendors…"
              data-testid={`${testId}-search`}
              style={{
                width: "100%",
                background: "#FCFAF5",
                border: "1px solid #E4DCCD",
                borderRadius: 10,
                padding: "10px 12px 10px 34px",
                fontSize: 13.5,
                color: "var(--ds-text)",
                outline: "none",
              }}
            />
          </div>
          <div style={{ overflowY: "auto", flex: 1 }}>
            {filtered.length === 0 ? (
              <div
                data-testid={`${testId}-empty`}
                style={{ padding: "24px 6px", textAlign: "center", fontSize: 13, fontStyle: "italic", color: "#A39A8B" }}
              >
                {list.length === 0
                  ? `No vendors offer "${serviceType}". Add this service to a vendor in the Catalog.`
                  : `No vendors match "${q}".`}
              </div>
            ) : (
              <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
                {filtered.map((v) => (
                  <li key={v.id}>
                    <button
                      onClick={() => onPick(v.id)}
                      data-testid={`vendor-pick-${v.id}`}
                      style={{
                        display: "block", width: "100%", textAlign: "left",
                        background: "#FCFAF5",
                        border: "1px solid #E4DCCD",
                        borderRadius: 10,
                        padding: "11px 12px",
                        cursor: "pointer",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: "var(--ds-ink)" }}>{v.name}</span>
                        {v.rating_count > 0 ? (
                          <span
                            data-testid={`vendor-pick-${v.id}-rating`}
                            style={{ fontSize: 9.5, letterSpacing: "0.12em", textTransform: "uppercase", padding: "2px 6px", background: "var(--brand-paper-soft)", color: "var(--brand-champagne)", borderRadius: 4 }}
                          >
                            ★ {v.avg_rating?.toFixed(1)} · {v.rating_count}
                          </span>
                        ) : (
                          <span
                            data-testid={`vendor-pick-${v.id}-new`}
                            style={{ fontSize: 9.5, letterSpacing: "0.12em", textTransform: "uppercase", padding: "2px 6px", background: "var(--brand-paper-soft)", color: "var(--brand-muted)", borderRadius: 4 }}
                          >
                            New
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 11.5, color: "#A39A8B", marginTop: 3 }}>
                        {v.type === "agency" ? `Agency · cap ${v.crew_capacity}` : "Individual"} · {v.phone_display || "—"}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <div
      data-testid={testId}
      onMouseLeave={onClose}
      style={{
        position: "absolute",
        zIndex: 50,
        isolation: "isolate",
        top: "calc(100% + 4px)",
        left: 0,
        width: 320,
        maxHeight: 288,
        overflowY: "auto",
        background: "#FFFFFF",
        border: "1px solid #EDE5D8",
        borderRadius: 10,
        boxShadow: "0 10px 24px rgba(60,46,20,.18)",
      }}
    >
      <div
        style={{
          padding: "10px 14px",
          borderBottom: "1px solid #EDE5D8",
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.13em",
          textTransform: "uppercase",
          color: "#A39A8B",
          background: "#FFFFFF",
        }}
      >
        VENDORS FOR {(serviceType || "").toUpperCase()}
      </div>
      {list.length === 0 ? (
        <div
          data-testid={`${testId}-empty`}
          style={{ padding: "14px", fontSize: 12, fontStyle: "italic", color: "#A39A8B", background: "#FFFFFF" }}
        >
          No vendors offer “{serviceType}”. Add this service to a vendor in the Catalog.
        </div>
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {list.map((v, idx) => (
            <li
              key={v.id}
              style={{
                display: "block",
                borderBottom: idx === list.length - 1 ? "none" : "1px solid #F0EAE0",
                background: "#FFFFFF",
              }}
            >
              <button
                onClick={() => onPick(v.id)}
                data-testid={`vendor-pick-${v.id}`}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: "10px 14px",
                  fontSize: 13,
                  background: "#FFFFFF",
                  transition: "background-color .12s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#FAF6EC")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "#FFFFFF")}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 500, color: "#2A2722" }}>
                  <span>{v.name}</span>
                  {/* Phase 5 Slice B-2 — inline rating badge */}
                  {v.rating_count > 0 ? (
                    <span
                      data-testid={`vendor-pick-${v.id}-rating`}
                      style={{
                        fontSize: 10,
                        letterSpacing: "0.16em",
                        textTransform: "uppercase",
                        padding: "2px 6px",
                        background: "var(--brand-paper-soft)",
                        color: "var(--brand-champagne)",
                        borderRadius: 4,
                      }}
                    >
                      ★ {v.avg_rating?.toFixed(1)} · {v.rating_count}
                    </span>
                  ) : (
                    <span
                      data-testid={`vendor-pick-${v.id}-new`}
                      style={{
                        fontSize: 10,
                        letterSpacing: "0.16em",
                        textTransform: "uppercase",
                        padding: "2px 6px",
                        background: "var(--brand-paper-soft)",
                        color: "var(--brand-muted)",
                        borderRadius: 4,
                      }}
                    >
                      New
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: "#A39A8B", marginTop: 2 }}>
                  {v.type === "agency" ? `Agency · cap ${v.crew_capacity}` : "Individual"} · {v.phone_display || "—"}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function EventModal({ mode, event, eventTypes, projectId, onClose }) {
  const [type, setType] = useState(event?.type || "");
  const [customType, setCustomType] = useState(eventTypes.length && !event?.type ? false : !!event && !eventTypes.includes(event.type));
  const [date, setDate] = useState(event?.date || "");
  const [venue, setVenue] = useState(event?.venue || "");
  const [startTime, setStartTime] = useState(event?.start_time || "");
  const [endTime, setEndTime] = useState(event?.end_time || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!type.trim()) { toast.error("Pick or enter an event type"); return; }
    setSaving(true);
    try {
      const body = { type, date: date || null, venue: venue || null, start_time: startTime || null, end_time: endTime || null };
      if (mode === "create") await api.post(`/projects/${projectId}/events`, body);
      else await api.patch(`/projects/${projectId}/events/${event.id}`, body);
      toast.success(mode === "create" ? "Event added" : "Event saved");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(14,48,48,0.4)" }} onClick={onClose} data-testid="event-modal">
      <div className="bg-white w-full max-w-[500px] p-7" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-2xl" style={{ color: "var(--brand-ink)" }}>{mode === "create" ? "Add event" : "Edit event"}</h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="space-y-4 text-sm">
          <label className="block">
            <div className="kicker mb-1.5">Event type</div>
            {customType ? (
              <div className="flex gap-2">
                <input value={type} onChange={(e) => setType(e.target.value)} placeholder="Custom event"
                       className="flex-1 px-3 py-2 border border-brand-border bg-white" style={{ borderRadius: 6 }}
                       data-testid="event-modal-custom-type" autoFocus />
                <button onClick={() => { setCustomType(false); setType(""); }} className="text-xs underline text-brand-muted">Pick</button>
              </div>
            ) : (
              <div className="flex gap-2">
                <select value={type} onChange={(e) => setType(e.target.value)} className="flex-1 px-3 py-2 border border-brand-border bg-white" style={{ borderRadius: 6 }} data-testid="event-modal-type">
                  <option value="">Pick…</option>
                  {eventTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <button onClick={() => { setCustomType(true); setType(""); }} className="text-xs underline text-brand-muted" data-testid="event-modal-custom-toggle">Custom…</button>
              </div>
            )}
          </label>
          <label className="block">
            <div className="kicker mb-1.5">Date</div>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full px-3 py-2 border border-brand-border bg-white" style={{ borderRadius: 6 }} data-testid="event-modal-date" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="kicker mb-1.5">Start</div>
              <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="w-full px-3 py-2 border border-brand-border bg-white" style={{ borderRadius: 6 }} data-testid="event-modal-start" />
            </label>
            <label className="block">
              <div className="kicker mb-1.5">End</div>
              <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="w-full px-3 py-2 border border-brand-border bg-white" style={{ borderRadius: 6 }} data-testid="event-modal-end" />
            </label>
          </div>
          <label className="block">
            <div className="kicker mb-1.5">Venue</div>
            <input value={venue} onChange={(e) => setVenue(e.target.value)} className="w-full px-3 py-2 border border-brand-border bg-white" style={{ borderRadius: 6 }} data-testid="event-modal-venue" />
          </label>
        </div>
        <div className="flex gap-3 mt-6 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-brand-border">Cancel</button>
          <button onClick={save} disabled={saving} className="px-5 py-2 text-sm font-medium text-white" style={{ background: "var(--brand-primary)" }} data-testid="event-modal-save">{saving ? "Saving…" : (mode === "create" ? "Add" : "Save")}</button>
        </div>
      </div>
    </div>
  );
}

function ConfirmationRow({ c, projectId, onSent }) {
  const [expanded, setExpanded] = useState(false);
  const handleSend = async () => {
    if (c.wa_link) window.open(c.wa_link, "_blank");
    try {
      await api.post(`/projects/${projectId}/confirmations/mark-sent`, { vendor_id: c.vendor_id });
      toast.success("Marked as sent");
      onSent();
    } catch (err) { toast.error("Failed to mark sent"); }
  };
  const handleMail = () => {
    if (!c.email) { toast.info("No email on file"); return; }
    const subject = encodeURIComponent("Booking confirmation");
    const body = encodeURIComponent(c.message);
    window.location.href = `mailto:${c.email}?subject=${subject}&body=${body}`;
  };
  return (
    <div className="border border-brand-border p-3" data-testid={`confirmation-row-${c.vendor_id}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm">{c.name}</div>
          <div className="text-xs text-brand-muted">{c.phone || "—"} · {c.services.length} assignment{c.services.length === 1 ? "" : "s"}</div>
        </div>
        {c.confirmation_sent_at ? (
          <span className="text-xs text-brand-muted" data-testid={`confirmation-sent-${c.vendor_id}`}>Sent {fmtDate(c.confirmation_sent_at)}</span>
        ) : (
          <>
            <button onClick={handleSend} className="px-3 py-1.5 text-xs font-medium text-white flex items-center gap-1" style={{ background: "var(--brand-success)" }} data-testid={`confirmation-wa-${c.vendor_id}`}>
              <Send size={11} /> WhatsApp
            </button>
            <button onClick={handleMail} className="px-3 py-1.5 text-xs border border-brand-border flex items-center gap-1" data-testid={`confirmation-mail-${c.vendor_id}`}>
              <Mail size={11} /> Mail
            </button>
          </>
        )}
        <button onClick={() => setExpanded(!expanded)} className="text-xs underline text-brand-muted">{expanded ? "Hide" : "Preview"}</button>
      </div>
      {expanded && (
        <div className="mt-3 p-3 bg-brand-paper border border-brand-border text-xs whitespace-pre-wrap" data-testid={`confirmation-preview-${c.vendor_id}`}>
          {c.message}
        </div>
      )}
    </div>
  );
}

function PayoutsTable({ project, payouts, vendors, projectId, onChanged }) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const vendorById = Object.fromEntries((vendors || []).map((v) => [v.id, v]));
  // Aggregate cost per vendor (Phase 4.3 bug fix: was using raw IDs as names)
  const byVendor = {};
  for (const ev of project.events || []) {
    for (const s of ev.services || []) {
      if (s.vendor_id) {
        byVendor[s.vendor_id] = byVendor[s.vendor_id] || { cost: 0 };
        byVendor[s.vendor_id].cost += parseInt(s.vendor_cost || 0, 10);
      }
    }
  }
  for (const p of payouts) {
    if (!byVendor[p.vendor_id]) byVendor[p.vendor_id] = { cost: 0 };
  }
  const paidByVendor = {};
  for (const p of payouts) paidByVendor[p.vendor_id] = (paidByVendor[p.vendor_id] || 0) + (p.amount || 0);

  const rows = Object.entries(byVendor).map(([vid, info]) => {
    const paid = paidByVendor[vid] || 0;
    const rawOwed = info.cost - paid;
    // Phase 4.3 — last_paid stamp: most recent payout.paid_at for this vendor on this project
    const vendorPayouts = payouts.filter((p) => p.vendor_id === vid);
    const lastPaidAt = vendorPayouts.length
      ? vendorPayouts.map((p) => p.paid_at).filter(Boolean).sort().reverse()[0]
      : null;
    return {
      vendor_id: vid,
      name: vendorById[vid]?.name || (payouts.find((p) => p.vendor_id === vid)?.vendor_name) || vid,
      cost: info.cost,
      paid,
      owed: Math.max(0, rawOwed),
      overpaid: rawOwed < 0,
      last_paid_at: lastPaidAt,
    };
  });

  const remove = async (payoutId) => {
    if (!window.confirm("Delete this payout?")) return;
    try {
      await api.delete(`/projects/${projectId}/payouts/${payoutId}`);
      onChanged();
    } catch (err) {
      // Same try/catch shape as the event/service destructive handlers —
      // prevents an uncaught axios rejection from being intercepted by
      // react-error-overlay before our global 403 interceptor can quiet it.
      if (err?.response?.status !== 403) {
        toast.error(err?.response?.data?.detail || "Delete failed");
      }
    }
  };

  // ═══ Mobile stacked-card layout (Pass 2B §10) ═════════════════════════
  if (isMobile) {
    return (
      <div className="space-y-4">
        <div data-testid="payouts-summary" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {rows.length === 0 ? (
            <div className="italic text-brand-muted py-3 text-center text-sm">No vendors assigned yet.</div>
          ) : rows.map((r) => (
            <div
              key={r.vendor_id}
              data-testid={`payouts-row-${r.vendor_id}`}
              style={{
                background: "#FFFDFA",
                borderRadius: 14,
                padding: "13px 14px",
                boxShadow: "0 4px 12px rgba(34,56,44,0.05), 0 1px 2px rgba(34,56,44,0.04)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 10 }}>
                <button
                  className="hover:underline text-left"
                  onClick={() => navigate(`/vendors/${r.vendor_id}`)}
                  data-testid={`payouts-vendor-link-${r.vendor_id}`}
                  style={{
                    fontFamily: "var(--ds-num-font)",
                    fontSize: 15,
                    fontWeight: 500,
                    color: "var(--ds-ink)",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                    minWidth: 0,
                    flex: 1,
                    textAlign: "left",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {r.name}
                </button>
                {r.overpaid && (
                  <span
                    className="px-1.5 py-0.5 text-[9px] tracking-[0.16em] uppercase"
                    style={{ background: "var(--brand-champagne)", color: "#fff", flexShrink: 0 }}
                    data-testid={`payouts-overpaid-${r.vendor_id}`}
                  >
                    OVERPAID
                  </span>
                )}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <MobilePayoutCell label="COST" value={fmtINR(r.cost)} />
                <MobilePayoutCell label="PAID" value={fmtINR(r.paid)} />
                <MobilePayoutCell
                  label="OWED"
                  value={fmtINR(r.owed)}
                  color={r.owed > 0 ? "var(--brand-danger)" : "var(--brand-success)"}
                />
                <MobilePayoutCell
                  label="LAST PAID"
                  testId={`payouts-last-paid-${r.vendor_id}`}
                  valueNode={r.last_paid_at ? <LastPaidStamp at={r.last_paid_at} /> : <span className="italic" style={{ color: "#B0A891" }}>—</span>}
                  small
                />
              </div>
            </div>
          ))}
        </div>
        {payouts.length > 0 && (
          <div>
            <div className="kicker mb-2">PAYOUT HISTORY</div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
              {payouts.map((p) => (
                <li
                  key={p.id}
                  data-testid={`payout-entry-${p.id}`}
                  style={{
                    background: "#FFFDFA",
                    borderRadius: 12,
                    padding: "10px 12px",
                    boxShadow: "0 2px 6px rgba(34,56,44,0.04)",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    fontSize: 13,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, color: "var(--ds-ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {vendorById[p.vendor_id]?.name || p.vendor_name || p.vendor_id}
                    </div>
                    <div style={{ fontSize: 11.5, color: "var(--ds-text-2)", marginTop: 1 }}>
                      {fmtDate(p.paid_at)} · {p.mode}{p.reference && ` · #${p.reference}`}
                    </div>
                  </div>
                  <span style={{ fontFamily: "var(--ds-num-font)", fontSize: 15, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
                    {fmtINR(p.amount)}
                  </span>
                  <button
                    onClick={() => remove(p.id)}
                    data-testid={`payout-delete-${p.id}`}
                    style={{ background: "transparent", border: "none", cursor: "pointer", color: "#A39A8B", padding: 4, flexShrink: 0 }}
                  >
                    <Trash2 size={13} />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <table className="w-full text-sm" data-testid="payouts-summary">
        <thead className="text-[10px] tracking-[0.18em] uppercase text-brand-muted">
          <tr><th className="text-left py-2">Vendor</th><th className="text-right py-2">Cost</th><th className="text-right py-2">Paid</th><th className="text-right py-2">Owed</th><th className="text-right py-2">Last paid</th></tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan="5" className="py-3 text-center italic text-brand-muted">No vendors assigned yet.</td></tr>
          ) : rows.map((r) => (
            <tr key={r.vendor_id} className="border-t border-brand-border" data-testid={`payouts-row-${r.vendor_id}`}>
              <td className="py-2">
                <button className="hover:underline" onClick={() => navigate(`/vendors/${r.vendor_id}`)} data-testid={`payouts-vendor-link-${r.vendor_id}`}>{r.name}</button>
                {r.overpaid && <span className="ml-2 px-1.5 py-0.5 text-[9px] tracking-[0.18em] uppercase" style={{ background: "var(--brand-champagne)", color: "#fff" }} data-testid={`payouts-overpaid-${r.vendor_id}`}>OVERPAID</span>}
              </td>
              <td className="py-2 text-right">{fmtINR(r.cost)}</td>
              <td className="py-2 text-right">{fmtINR(r.paid)}</td>
              <td className="py-2 text-right" style={{ color: r.owed > 0 ? "var(--brand-danger)" : "var(--brand-success)" }}>{fmtINR(r.owed)}</td>
              <td className="py-2 text-right text-xs text-brand-muted" data-testid={`payouts-last-paid-${r.vendor_id}`}>
                {r.last_paid_at ? <LastPaidStamp at={r.last_paid_at} /> : <span className="italic">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {payouts.length > 0 && (
        <div>
          <div className="kicker mb-2">PAYOUT HISTORY</div>
          <ul className="space-y-1 text-sm">
            {payouts.map((p) => (
              <li key={p.id} className="flex items-center gap-3 py-1.5 border-b border-brand-border last:border-0" data-testid={`payout-entry-${p.id}`}>
                <span>{fmtDate(p.paid_at)}</span>
                <span>{vendorById[p.vendor_id]?.name || p.vendor_name || p.vendor_id}</span>
                <span className="text-brand-muted">{p.mode}</span>
                {p.reference && <span className="text-brand-muted text-xs">#{p.reference}</span>}
                <span className="ml-auto">{fmtINR(p.amount)}</span>
                <button onClick={() => remove(p.id)} data-testid={`payout-delete-${p.id}`}><Trash2 size={12} className="text-brand-muted hover:text-brand-rose" /></button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function LastPaidStamp({ at }) {
  if (!at) return null;
  const d = new Date(at);
  const today = new Date();
  d.setHours(0, 0, 0, 0); today.setHours(0, 0, 0, 0);
  const days = Math.round((today - d) / (1000 * 60 * 60 * 24));
  const rel = days <= 0 ? "today" : days === 1 ? "1d ago" : `${days}d ago`;
  // Warn color if > 14 days
  const color = days > 14 ? "var(--brand-danger)" : "var(--brand-muted)";
  return (
    <span style={{ color }} title={`Last paid: ${at}`}>
      {rel}
    </span>
  );
}

function PayoutModal({ projectId, project, vendors, onClose }) {
  // Vendors assigned to at least one service in this project
  const assignedVendorIds = new Set();
  for (const ev of project.events || []) for (const s of ev.services || []) if (s.vendor_id) assignedVendorIds.add(s.vendor_id);
  const assignedVendors = vendors.filter((v) => assignedVendorIds.has(v.id));

  const [vendorId, setVendorId] = useState(assignedVendors[0]?.id || "");
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("UPI");
  const [reference, setReference] = useState("");
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const amt = parseInt(amount || "0", 10);
    if (!vendorId || amt <= 0 || !paidAt) { toast.error("vendor, amount and date required"); return; }
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/payouts`, { vendor_id: vendorId, amount: amt, mode, reference: reference || null, paid_at: paidAt, note: note || null });
      toast.success("Payout recorded");
      onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(14,48,48,0.4)" }} onClick={onClose} data-testid="payout-modal">
      <div className="bg-white w-full max-w-[480px] p-7" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-2xl" style={{ color: "var(--brand-ink)" }}>Record payout</h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="space-y-4 text-sm">
          <label className="block">
            <div className="kicker mb-1.5">Vendor</div>
            <select value={vendorId} onChange={(e) => setVendorId(e.target.value)} className="w-full px-3 py-2 border border-brand-border bg-white" style={{ borderRadius: 6 }} data-testid="payout-vendor">
              {assignedVendors.length === 0 && <option value="">No vendors assigned</option>}
              {assignedVendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="kicker mb-1.5">Amount ₹</div>
              <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full px-3 py-2 border border-brand-border bg-white" style={{ borderRadius: 6 }} data-testid="payout-amount" />
            </label>
            <label className="block">
              <div className="kicker mb-1.5">Mode</div>
              <select value={mode} onChange={(e) => setMode(e.target.value)} className="w-full px-3 py-2 border border-brand-border bg-white" style={{ borderRadius: 6 }} data-testid="payout-mode">
                {PAYOUT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="kicker mb-1.5">Paid on</div>
              <input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} className="w-full px-3 py-2 border border-brand-border bg-white" style={{ borderRadius: 6 }} data-testid="payout-date" />
            </label>
            <label className="block">
              <div className="kicker mb-1.5">Reference</div>
              <input value={reference} onChange={(e) => setReference(e.target.value)} className="w-full px-3 py-2 border border-brand-border bg-white" style={{ borderRadius: 6 }} data-testid="payout-reference" />
            </label>
          </div>
          <label className="block">
            <div className="kicker mb-1.5">Note</div>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} className="w-full px-3 py-2 border border-brand-border bg-white" rows="2" style={{ borderRadius: 6 }} data-testid="payout-note" />
          </label>
        </div>
        <div className="flex gap-3 mt-6 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-brand-border">Cancel</button>
          <button onClick={save} disabled={saving || !vendorId} className="px-5 py-2 text-sm font-medium text-white disabled:bg-brand-muted" style={{ background: "var(--brand-primary)" }} data-testid="payout-save">{saving ? "Saving…" : "Record"}</button>
        </div>
      </div>
    </div>
  );
}

function DoubleBookWarningModal({ payload, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(14,48,48,0.4)" }} onClick={onCancel} data-testid="doublebook-modal">
      <div className="bg-white w-full max-w-[480px] p-7" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-4">
          <AlertTriangle size={22} color="var(--brand-champagne)" />
          <h2 className="font-display text-2xl" style={{ color: "var(--brand-ink)" }}>Possible double-booking</h2>
        </div>
        <p className="text-sm mb-4" data-testid="doublebook-detail">{payload.details}</p>
        {payload.clients_affected && payload.clients_affected.length > 0 && (
          <ul className="text-xs text-brand-muted space-y-1 mb-5">
            {payload.clients_affected.map((c, i) => (
              <li key={i}>· {c.client_name || c.lead_id} · {c.event_date}</li>
            ))}
          </ul>
        )}
        <div className="flex gap-3 justify-end">
          <button onClick={onCancel} className="px-4 py-2 text-sm border border-brand-border" data-testid="doublebook-cancel">Cancel</button>
          <button onClick={onConfirm} className="px-5 py-2 text-sm font-medium text-white" style={{ background: "var(--brand-champagne)" }} data-testid="doublebook-confirm">Assign anyway</button>
        </div>
      </div>
    </div>
  );
}

function DeliverablesList({ project }) {
  const items = [];
  for (const ev of project.events || []) {
    for (const svc of ev.services || []) {
      for (const d of svc.deliverables || []) {
        items.push({ ...d, service_type: svc.type, event_type: ev.type });
      }
    }
  }
  if (items.length === 0) return <p className="text-sm text-brand-muted italic">No deliverables for this project's services.</p>;
  return (
    <ul className="space-y-1.5 text-sm" data-testid="deliverables-list">
      {items.map((d) => (
        <li key={d.id} className="flex items-center gap-3 py-1.5 border-b border-brand-border last:border-0">
          <span>{d.event_type} · {d.service_type} · {d.label}</span>
          <span className="text-brand-muted">{d.expected_qty} {d.unit}</span>
          <span className="ml-auto text-brand-muted text-xs">Due {fmtDate(d.due_at)}</span>
          <span className="px-2 py-0.5 text-[10px] tracking-[0.16em] uppercase" style={{ background: d.status === "delivered" ? "var(--brand-success-bg)" : "var(--brand-paper-soft)", color: d.status === "delivered" ? "var(--brand-success)" : "var(--brand-muted)" }}>
            {d.status.replace("_", " ")}
          </span>
        </li>
      ))}
    </ul>
  );
}

function PaymentScheduleDraft({ schedule }) {
  const [open, setOpen] = useState(false);
  const total = (schedule.installments || []).reduce((a, b) => a + (b.amount || 0), 0);
  const typeLabel = {
    "50_50": "50 / 50",
    "30_30_40": "30 / 30 / 40",
    "full": "Full Payment",
    "custom": "Custom",
  }[schedule.type] || schedule.type;

  return (
    <div className="ds-pill mb-6" data-testid="payment-schedule-draft">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-3">
        <div className="flex items-baseline gap-3">
          <div className="kicker">PAYMENT SCHEDULE (DRAFT)</div>
          <div className="text-sm">{typeLabel} · {(schedule.installments || []).length} installment{(schedule.installments || []).length === 1 ? "" : "s"} · {fmtINR(total)}</div>
        </div>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <div className="px-5 pb-4 border-t border-brand-border" data-testid="payment-schedule-installments">
          <div className="text-[10px] tracking-[0.18em] uppercase text-brand-muted py-3">DRAFT SCHEDULE — PAYMENT TRACKING COMING IN NEXT PHASE</div>
          <ul className="space-y-1 text-sm">
            {(schedule.installments || []).map((i, idx) => (
              <li key={idx} className="flex items-center gap-3 py-1.5 border-b border-brand-border last:border-0">
                <span>{i.label || `Installment ${idx + 1}`}</span>
                <span className="text-brand-muted text-xs">{i.due_at || "no date"}</span>
                {i.due_at_source === "fixed" && (
                  <span className="px-1.5 py-0.5 text-[9px] tracking-[0.18em] uppercase" style={{ background: "var(--brand-champagne)", color: "#fff" }}>EDITED</span>
                )}
                <span className="ml-auto">{fmtINR(i.amount || 0)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function CancelProjectModal({ clientName, partnerName, advanceAmount, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(14,48,48,0.45)" }} onClick={onCancel} data-testid="cancel-project-modal">
      <div className="bg-white w-full max-w-[480px] p-7" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-display text-2xl mb-3" style={{ color: "var(--brand-ink)" }} data-testid="cancel-project-title">Cancel project?</h2>
        <p className="text-sm text-brand-muted mb-6" data-testid="cancel-project-body">
          {clientName}{partnerName ? ` × ${partnerName}` : ""}'s project will be marked as Cancelled. The lead will move back to "Send Proposal" so a new proposal can be built later.
          {advanceAmount > 0 && (
            <> The recorded advance ({fmtINR(advanceAmount)}) will stay on the cancelled project as a historical record.</>
          )}
        </p>
        <div className="flex justify-end gap-3">
          <button onClick={onCancel} className="px-4 py-2 text-sm border border-brand-border" data-testid="cancel-project-dismiss">Cancel</button>
          <button onClick={onConfirm} className="px-5 py-2 text-sm font-medium text-white" style={{ background: "var(--brand-danger)" }} data-testid="cancel-project-confirm">Yes, cancel project</button>
        </div>
      </div>
    </div>
  );
}

// Phase 5 Wizard Step 3 Insertion patch — inline editable sales notes.
function SalesNotesSection({ projectId, initial }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(initial || "");
  const [saved, setSaved] = useState(initial || "");

  useEffect(() => { setVal(initial || ""); setSaved(initial || ""); }, [initial]);

  const save = async () => {
    try {
      const trimmed = (val || "").slice(0, 500);
      const { data } = await api.patch(`/projects/${projectId}/sales-notes`, { sales_notes: trimmed || null });
      setSaved(data.sales_notes || "");
      setVal(data.sales_notes || "");
      setEditing(false);
      toast.success("Sales notes saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const cancel = () => { setVal(saved); setEditing(false); };

  return (
    <div className="ds-pill p-6 mb-10" data-testid="project-sales-notes">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-xl" style={{ color: "var(--brand-ink)" }}>Sales notes</h2>
        {!editing && (
          <button onClick={() => setEditing(true)} className="text-xs underline text-brand-muted" data-testid="sales-notes-edit">
            {saved ? "Edit" : "+ Add notes"}
          </button>
        )}
      </div>
      {editing ? (
        <div className="space-y-2">
          <textarea value={val} onChange={(e) => setVal(e.target.value.slice(0, 500))}
                    rows={3} maxLength={500}
                    placeholder="e.g. Discussed 800 photos but client wants to see samples first."
                    className="w-full px-3 py-2 text-sm border border-brand-border bg-white"
                    style={{ borderRadius: 6 }} data-testid="sales-notes-textarea" />
          <div className="flex items-center justify-between text-xs text-brand-muted">
            <span>{val.length} / 500</span>
            <div className="flex gap-2">
              <button onClick={cancel} className="px-3 py-1 border border-brand-border" data-testid="sales-notes-cancel">Cancel</button>
              <button onClick={save} className="px-3 py-1 text-white" style={{ background: "var(--brand-primary)" }} data-testid="sales-notes-save">Save</button>
            </div>
          </div>
        </div>
      ) : saved ? (
        <p className="text-sm text-brand-ink whitespace-pre-wrap" data-testid="sales-notes-text">{saved}</p>
      ) : (
        <p className="text-sm text-brand-muted italic" data-testid="sales-notes-empty">No sales notes recorded.</p>
      )}
    </div>
  );
}



// Phase 5 Slice B-2 — completion sign-off chain UI.
// Horizontal strip with the four phases + per-chip Undo + Close-project button.
const SIGNOFF_PHASES = ["sales", "production", "delivery", "accounts"];
const SIGNOFF_LABELS = {
  sales: "Sales", production: "Production", delivery: "Delivery", accounts: "Accounts",
};

function SignoffPanel({ project, onChanged }) {
  const projectId = project.id;
  const so = project.completion_signoffs || {};
  const allDone = SIGNOFF_PHASES.every((p) => (so[p] || {}).completed);
  const pendingLabels = SIGNOFF_PHASES.filter((p) => !(so[p] || {}).completed)
    .map((p) => SIGNOFF_LABELS[p]);

  const [revertModal, setRevertModal] = useState(null);  // phase being reverted
  const [closeModalOpen, setCloseModalOpen] = useState(false);

  const closeProject = async () => {
    try {
      await api.patch(`/projects/${projectId}`, { status: "Completed" });
      toast.success("Project marked Completed");
      onChanged && onChanged();
      setCloseModalOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Close failed");
    }
  };

  return (
    <div className="ds-pill px-6 py-4 mb-8 flex flex-wrap items-center justify-between gap-4"
         data-testid="signoff-panel">
      <div className="flex flex-wrap gap-3 items-center" data-testid="signoff-chips">
        {SIGNOFF_PHASES.map((phase) => {
          const entry = so[phase] || { completed: false };
          const ok = !!entry.completed;
          const tooltip = ok
            ? `Completed${entry.completed_by ? ` by ${entry.completed_by}` : ""}${entry.completed_at ? ` on ${new Date(entry.completed_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}` : ""}`
            : "Pending";
          return (
            <div key={phase} className="flex items-center gap-2" title={tooltip}
                 data-testid={`signoff-chip-${phase}`}>
              <span className="text-base"
                    style={{ color: ok ? "#059669" : "var(--brand-champagne)" }}>
                {ok ? "✓" : "⏳"}
              </span>
              <span className="text-sm" style={{ color: "var(--brand-ink)" }}>
                {SIGNOFF_LABELS[phase]}
              </span>
              {phase === "accounts" && (
                <span className="px-1.5 py-0.5 text-[8px] tracking-[0.18em] uppercase"
                      style={{ background: "var(--brand-paper)", color: "var(--brand-muted)" }}
                      data-testid="signoff-auto-pill-accounts">
                  AUTO
                </span>
              )}
              {ok && phase !== "sales" && phase !== "accounts" && (
                <button onClick={() => setRevertModal(phase)}
                        className="text-[10px] underline text-brand-muted"
                        data-testid={`signoff-revert-${phase}`}>
                  Undo
                </button>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        <Gated resource="close_project">
        <button
          onClick={() => setCloseModalOpen(true)}
          disabled={!allDone}
          title={!allDone ? `Awaiting sign-off: ${pendingLabels.join(", ")}` : ""}
          className="px-3 py-2 text-xs tracking-[0.18em] uppercase text-white disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: "var(--brand-primary)" }}
          data-testid="close-project-button">
          Close project
        </button>
        </Gated>
      </div>

      {revertModal && (
        <RevertSignoffModal
          phase={revertModal}
          projectId={projectId}
          onClose={() => setRevertModal(null)}
          onReverted={() => { setRevertModal(null); onChanged && onChanged(); }}
        />
      )}
      {closeModalOpen && (
        <CloseProjectModal
          projectName={project.partner_name ? `${project.partner_name}` : projectId}
          onClose={() => setCloseModalOpen(false)}
          onConfirm={closeProject}
        />
      )}
    </div>
  );
}

function RevertSignoffModal({ phase, projectId, onClose, onReverted }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (note.trim().length < 10) {
      setError("Please add a note (at least 10 characters)");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await api.post(`/projects/${projectId}/signoffs/${phase}/revert`, { note: note.trim() });
      toast.success(`${SIGNOFF_LABELS[phase]} reopened`);
      onReverted();
    } catch (err) {
      setError(err?.response?.data?.detail || "Revert failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: "rgba(14,48,48,0.5)" }}
         data-testid="revert-signoff-modal">
      <div className="bg-white p-6 w-[min(440px,92vw)]" style={{ borderRadius: 6 }}
           onClick={(e) => e.stopPropagation()}>
        <div className="kicker mb-3" data-testid="revert-signoff-title">
          MARK {SIGNOFF_LABELS[phase].toUpperCase()} AS NOT YET COMPLETE?
        </div>
        <p className="text-xs text-brand-muted mb-3">
          The project status will roll back to a consistent state. Add a reason
          for the activity log:
        </p>
        <textarea
          value={note} onChange={(e) => setNote(e.target.value)}
          rows={3} placeholder="e.g. Forgot to capture Sangeet event"
          className="w-full px-3 py-2 text-sm border border-brand-border"
          style={{ borderRadius: 6 }}
          data-testid="revert-signoff-note"
        />
        <div className="text-[10px] text-brand-muted text-right mt-1">
          {note.trim().length} / min 10 chars
        </div>
        {error && <p className="text-xs text-rose-700 mt-2" data-testid="revert-signoff-error">{error}</p>}
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} disabled={saving}
                  className="px-3 py-2 text-xs tracking-[0.18em] uppercase text-brand-muted"
                  data-testid="revert-signoff-cancel">Cancel</button>
          <button onClick={submit} disabled={saving || note.trim().length < 10}
                  className="px-3 py-2 text-xs tracking-[0.18em] uppercase text-white disabled:opacity-50"
                  style={{ background: "var(--brand-danger, #b91c1c)" }}
                  data-testid="revert-signoff-confirm">
            {saving ? "Reverting…" : "Confirm revert"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CloseProjectModal({ projectName, onClose, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: "rgba(14,48,48,0.5)" }}
         data-testid="close-project-modal">
      <div className="bg-white p-6 w-[min(420px,92vw)]" style={{ borderRadius: 6 }}
           onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display text-xl mb-3" style={{ color: "var(--brand-ink)" }}>
          Close project?
        </h3>
        <p className="text-sm text-brand-muted mb-5">
          This marks <span className="text-brand-ink font-medium">{projectName}</span> as <em>Completed</em> and archives it.
        </p>
        <div className="flex justify-end gap-2">
          <button onClick={onClose}
                  className="px-3 py-2 text-xs tracking-[0.18em] uppercase text-brand-muted"
                  data-testid="close-project-cancel">Cancel</button>
          <button onClick={onConfirm}
                  className="px-3 py-2 text-xs tracking-[0.18em] uppercase text-white"
                  style={{ background: "var(--brand-primary)" }}
                  data-testid="close-project-confirm">
            Close project
          </button>
        </div>
      </div>
    </div>
  );
}



// ═══════════════════════════════════════════════════════════════════════════
// Phase 7.1 Pass 2B — Mobile-only helpers for Project Detail.
// ═══════════════════════════════════════════════════════════════════════════

/** Labeled field wrapper used inside a mobile service card. */
function MobileServiceField({ label, children }) {
  return (
    <div>
      <div
        style={{
          fontSize: 9.5,
          fontWeight: 700,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "rgba(34,56,44,0.4)",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

/** One cell inside the mobile payouts 2×2 grid. */
function MobilePayoutCell({ label, value, valueNode, color, small, testId }) {
  return (
    <div data-testid={testId}>
      <div
        style={{
          fontSize: 9.5,
          fontWeight: 700,
          letterSpacing: "0.1em",
          color: "rgba(34,56,44,0.42)",
          marginBottom: 3,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: "var(--ds-num-font)",
          fontSize: small ? 13 : 17,
          fontWeight: 500,
          color: color || "var(--ds-ink)",
          fontVariantNumeric: "tabular-nums",
          lineHeight: 1.1,
        }}
      >
        {valueNode || value}
      </div>
    </div>
  );
}
